import moment from 'moment';
import { CONSTANTS } from 'src/components/Constants';
import { english } from 'src/i18n/language';
import { VARIANT_ORIGIN_OPTIONS } from './sections/clinical-notes-section/forms/formService';
import { classificationText } from './sections/genotypes-menu/classificationText';

// ─────────────────────────────────────────────────────────────────────────────
// Record Modification History — frontend formatter registry (Phase 1)
//
// The backend read model tags each change with a `type` (from the field
// registry sourced from import-schema.json + reviewed clinical map). Here we map
// that type → a display string + whether to render mono-spaced (HGVS). Enum
// value→label maps reuse the app's existing option lists; no field labels live
// here (those come from the backend `label`).
// ─────────────────────────────────────────────────────────────────────────────

// zygosity / carrier-status value → label (reuse the existing option list)
const ZYGOSITY = {};
(CONSTANTS.carrierStatuses || []).forEach((o) => {
  ZYGOSITY[o.value] = o.label;
});

// variant origin value → label, as the Test form offers it ('unknown' reads
// "Not stated", never "Unknown")
const ORIGIN = {};
VARIANT_ORIGIN_OPTIONS.forEach((o) => {
  ORIGIN[o.value] = o.label;
});

const humanise = (v) =>
  String(v)
    .replace(/[_-]+/g, ' ')
    .replace(/^./, (c) => c.toUpperCase());

/** HGVS values render mono-spaced. */
export const isMono = (type) => type === 'hgvs';

/**
 * Format a raw stored value for display given its renderer `type`.
 * Returns null for an "empty" value so callers can render their own
 * placeholder — never a literal "empty" string that could collide with real
 * data. Objects/arrays should never reach here (the backend only emits scalar
 * changes) but are guarded to null defensively rather than rendered as
 * "[object Object]".
 *
 * `t` is useAutoTranslation's (TGCLOUD-1893), for this app's own words: the
 * option lists' labels, Yes/No, Alive/Deceased, a count's noun and a
 * classification that is vocabulary. A value outside those lists is the
 * record's, humanised or shown as stored, and never sent to it. The
 * inheritance mode is a site's own lookup value, so it is never translated.
 */
export function formatValue(type, v, t = english) {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'object') return null;
  switch (type) {
    case 'enum:zygosity':
      return ZYGOSITY[v] ? t(ZYGOSITY[v]) : humanise(v);
    case 'enum:inheritance':
      return humanise(v);
    case 'enum:origin':
      return ORIGIN[v] ? t(ORIGIN[v]) : humanise(v);
    case 'enum:acmg':
      // Already stored as a label, e.g. "Pathogenic".
      return classificationText(String(v), t);
    case 'booleanAlias': {
      const s = String(v).toLowerCase();
      if (s === 'true') return t('Alive');
      if (s === 'false' || s === 'no') return t('Deceased');
      return humanise(v);
    }
    case 'boolean':
      return v === true || v === 'true' ? t('Yes') : t('No');
    case 'date':
    case 'partialDate': {
      const m = moment(v);
      return m.isValid() ? m.format('DD/MM/YYYY') : String(v);
    }
    case 'number':
      return String(v);
    case 'count': {
      // Array fields are summarised to an element count, never their content.
      const n = Number(v);
      return n === 1
        ? t('{{count}} item', { count: n })
        : t('{{count}} items', { count: n });
    }
    case 'hgvs':
    case 'text':
    default:
      return String(v);
  }
}
